<template>
  <div>
    <!-- Add Job Button -->
    <div class="q-mt-md">
      <div class="row-reverse">
        <q-btn
          color="primary"
          label="Add Job"
          @click="onAdd"
          class="q-mb-md q-ml-lg"
        />
      </div>
    </div>

    <!-- Job Table -->
    <BaseTable
      :rows="formattedJobs"
      :columns="columns"
      :total-rows="totalJobs"
      :get-edit-url="getEditUrl"
      search-placeholder="Search Current Table..."
    >
      <template #actions="{ row }">
        <q-td class="q-pa-none text-center">
          <!-- Add Ticket Button -->
          <q-btn
            v-if="row.has_tickets"
            color="primary"
            label="Add Ticket"
            @click="goToAddTicket(row.id)"
            class="q-mb-sm q-ml-sm"
            size="sm"
          />
          <!-- Delete Button -->
          <span v-else>
            <q-btn
              color="red"
              label="Delete"
              @click="confirmDelete(row)"
              class="q-mb-sm q-ml-sm"
              size="sm"
            />
            <q-btn
              color="primary"
              label="Add Ticket"
              @click="goToAddTicket(row.id)"
              class="q-mb-sm q-ml-sm"
              size="sm"
            />
          </span>
        </q-td>
      </template>
    </BaseTable>

    <!-- Pagination Controls -->
    <div class="row justify-left q-my-md q-ml-lg">
      <q-pagination
        v-model="currentPage"
        :max="totalPages"
        :max-pages="10"
        direction-links
        @update:model-value="onPageChange"
      />
    </div>

    <!-- Delete Confirmation Dialog -->
    <q-dialog v-model="deleteModalVisible">
      <q-card>
        <q-card-section>
          <div class="text-h6">Confirm Deletion</div>
        </q-card-section>

        <q-card-section>
          <p>Are you sure you want to delete <b>Job ID: {{ jobToDelete?.id }}</b>?</p>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Cancel" color="primary" @click="cancelDelete" />
          <q-btn flat label="Delete" color="negative" @click="onDeleteConfirmed" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import BaseTable from '@/components/BaseTable';
import axios from 'axios';
import { watch } from 'vue';
import { selectedYear } from '@/store/store';

export default {
  name: 'JobTable',
  components: {
    BaseTable,
  },
  data() {
    return {
      jobs: [],
      totalJobs: 0,
      deleteModalVisible: false,
      jobToDelete: null,

      // Pagination state
      currentPage: 1,
      pageSize: 50,
      totalPages: 1,

      columns: [
        { name: 'actions', align: 'center', label: 'Actions', field: 'actions', sortable: false },
        { name: 'date', align: 'left', label: 'Date', field: 'date', sortable: true },
        { name: 'customer', align: 'left', label: 'Customer', field: 'customer_id', sortable: true },
        { name: 'producer', align: 'left', label: 'Producer', field: 'producer_id', sortable: true },
        { name: 'crop', align: 'left', label: 'Crop', field: 'crop_id', sortable: true },
        { name: 'field', align: 'left', label: 'Field', field: 'field_id', sortable: true },
        { name: 'add_date', align: 'left', label: 'Add Date', field: 'add_date', sortable: true },
        { name: 'edit_date', align: 'left', label: 'Edit Date', field: 'edit_date', sortable: true },
      ],
    };
  },
  computed: {
    formattedJobs() {
      return this.jobs.map((job) => ({
        ...job,
        date: job.date || 'N/A',
        customer_id: job.customer_id?.name || 'N/A',
        producer_id: job.producer_id?.name || 'N/A',
        crop_id: job.crop_id?.crop || 'N/A',
        field_id: job.field_id?.name || 'N/A',
        add_date: new Date(job.add_date).toLocaleDateString(),
        edit_date: new Date(job.edit_date).toLocaleDateString(),
      }));
    },
  },
  created() {
    this.fetchJobs();
    watch(selectedYear, () => {
      this.currentPage = 1;
      this.fetchJobs();
    });
  },
  methods: {
    async fetchJobs() {
      try {
        const response = await axios.get('/api/v1/job/jobs/', {
          params: {
            year: selectedYear.value,
            page: this.currentPage,
            page_size: this.pageSize,
          },
        });

        this.jobs = response.data.results;
        this.totalJobs = response.data.count;
        this.totalPages = Math.ceil(this.totalJobs / this.pageSize);
      } catch (error) {
        console.error('Error fetching jobs:', error);
      }
    },
    onPageChange(page) {
      this.currentPage = page;
      this.fetchJobs();
    },
    goToAddTicket(jobId) {
      this.$router.push(`/crop_ticket/add/${jobId}`);
    },
    confirmDelete(row) {
      this.jobToDelete = row;
      this.deleteModalVisible = true;
    },
    cancelDelete() {
      this.deleteModalVisible = false;
      this.jobToDelete = null;
    },
    async onDeleteConfirmed() {
      if (!this.jobToDelete) return;

      try {
        await axios.delete(`/api/v1/job/jobs/${this.jobToDelete.id}/`);
        this.$q.notify({
          type: 'positive',
          message: `Job ID ${this.jobToDelete.id} deleted successfully.`,
        });
        this.fetchJobs();
      } catch (error) {
        console.error('Error deleting job:', error);
        this.$q.notify({
          type: 'negative',
          message: 'Failed to delete job.',
        });
      } finally {
        this.deleteModalVisible = false;
        this.jobToDelete = null;
      }
    },
    onAdd() {
      this.$router.push('/job/add');
    },
    getEditUrl(row) {
      return `/jobs/edit/${row.id}`;
    },
  },
};
</script>

<style scoped>
/* Ensures action buttons are aligned and table rows maintain borders */
.q-td {
  text-align: center;
}
</style>
